// Record a meeting in the browser. Two recorders share the microphone: one makes the whole recording (kept in memory,
// sent when it stops), the other makes a fresh, whole audio file every partSeconds and sends it at once, so the
// transcript and the notes fill in while people talk. The clock leaves out paused time; every part says where it
// starts in it. The screen is kept awake while recording (a phone that locks stops the microphone).
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMotionValue, type MotionValue } from 'motion/react';
import type { Recording } from '@dpw/shared';
import { api } from '../api';
import { micError } from '../lib/recorder';

export type MeetingRecState = 'idle' | 'asking' | 'recording' | 'paused' | 'sending' | 'done';
const typeFor = () => ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find(t => window.MediaRecorder.isTypeSupported?.(t)) ?? '';
const nameFor = (mime: string, base: string) => `${base}.${mime.includes('mp4') ? 'm4a' : 'webm'}`;
/** The whole recording is sent when it is at most this big (the upload limit); past it, the parts carry it. */
const WHOLE_MAX = 48 * 1024 * 1024;

type Wake = { release: () => Promise<void> };
interface Live {
  stream: MediaStream; ac: AudioContext; whole: MediaRecorder; part: MediaRecorder | null; chunks: Blob[]; mime: string;
  id: string; partSeconds: number; seq: number; partStart: number; elapsed: number; tick?: number; raf?: number; wake?: Wake | null; sends: Promise<unknown>[];
}

export function useMeetingRecorder() {
  const [state, setState] = useState<MeetingRecState>('idle');
  const [error, setError] = useState('');
  const [seconds, setSeconds] = useState(0);
  const [marks, setMarks] = useState<number[]>([]);
  const [rec, setRec] = useState<Recording | null>(null);
  const level = useMotionValue(0);
  const bars = useRef<number[]>(Array(64).fill(0));
  const r = useRef<Live | null>(null);

  const release = useCallback(() => {
    const x = r.current;
    if (!x) return;
    window.clearInterval(x.tick);
    cancelAnimationFrame(x.raf ?? 0);
    x.stream.getTracks().forEach(t => t.stop());
    void x.ac.close().catch(() => undefined);
    void x.wake?.release().catch(() => undefined);
    level.set(0);
  }, [level]);
  useEffect(() => () => release(), [release]);

  /** A new part recorder; when it stops, its file is sent with where it started. */
  const newPart = useCallback(() => {
    const x = r.current!;
    const p = new MediaRecorder(x.stream, x.mime ? { mimeType: x.mime, audioBitsPerSecond: 32_000 } : undefined);
    const chunks: Blob[] = [];
    const seq = x.seq++, start = x.elapsed;
    p.ondataavailable = e => { if (e.data.size) chunks.push(e.data); };
    p.onstop = () => {
      const blob = new Blob(chunks, { type: p.mimeType || x.mime || 'audio/webm' });
      if (!blob.size) return;
      // A part that does not arrive is not fatal: the whole recording is transcribed again at the end.
      x.sends.push(api.recordings.part(x.id, seq, start, blob, nameFor(blob.type, `part-${seq}`)).catch(() => undefined));
    };
    x.partStart = x.elapsed;
    x.part = p;
    p.start();
  }, []);

  const start = useCallback(async (target: { activityId?: string | null; trackId?: string | null; title?: string | null }) => {
    setError(''); setState('asking');
    let stream: MediaStream | null = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const started = await api.recordings.start(target);
      const ac = new AudioContext();
      const an = ac.createAnalyser();
      an.fftSize = 512;
      ac.createMediaStreamSource(stream).connect(an);
      const mime = typeFor();
      const whole = new MediaRecorder(stream, mime ? { mimeType: mime, audioBitsPerSecond: 32_000 } : undefined);
      const x: Live = { stream, ac, whole, part: null, chunks: [], mime, id: started.id, partSeconds: started.partSeconds, seq: 0, partStart: 0, elapsed: 0, sends: [], wake: null };
      r.current = x;
      whole.ondataavailable = e => { if (e.data.size) x.chunks.push(e.data); };
      whole.start(10_000);
      newPart();
      const buf = new Float32Array(an.fftSize);
      let eased = 0, frame = 0;
      const draw = () => {
        an.getFloatTimeDomainData(buf);
        let sum = 0;
        for (const v of buf) sum += v * v;
        eased += (Math.min(1, Math.pow(Math.sqrt(sum / buf.length) * 6, 0.7)) - eased) * 0.35;
        level.set(eased);
        if (++frame % 3 === 0) { bars.current.push(eased); bars.current.shift(); }
        x.raf = requestAnimationFrame(draw);
      };
      x.raf = requestAnimationFrame(draw);
      x.tick = window.setInterval(() => {
        if (x.whole.state !== 'recording') return;
        x.elapsed += 1;
        setSeconds(x.elapsed);
        // A new part every partSeconds: stop this one (it sends itself) and start the next.
        if (x.elapsed - x.partStart >= x.partSeconds) { x.part?.stop(); newPart(); }
      }, 1000);
      const wl = (navigator as Navigator & { wakeLock?: { request: (t: 'screen') => Promise<Wake> } }).wakeLock;
      x.wake = wl ? await wl.request('screen').catch(() => null) : null;
      setRec(started); setSeconds(0); setMarks([]); setState('recording');
    } catch (e) {
      stream?.getTracks().forEach(t => t.stop());
      setState('idle');
      setError(e instanceof DOMException ? micError(e) : e instanceof Error ? e.message : 'Could not start recording.');
    }
  }, [level, newPart]);

  const pause = useCallback(() => {
    const x = r.current;
    if (!x) return;
    if (x.whole.state === 'recording') { x.whole.pause(); x.part?.pause(); setState('paused'); }
    else if (x.whole.state === 'paused') { x.whole.resume(); x.part?.resume(); setState('recording'); }
  }, []);

  const mark = useCallback(() => { const x = r.current; if (x) setMarks(m => (m.includes(x.elapsed) ? m : [...m, x.elapsed])); }, []);

  /** Stop: the last part, then the whole recording, then stop (the minutes are written on the server). */
  const stop = useCallback(async (marked: number[]) => {
    const x = r.current;
    if (!x) return;
    setState('sending');
    window.clearInterval(x.tick);
    const stopped = (m: MediaRecorder | null) => (m && m.state !== 'inactive' ? new Promise<void>(res => { m.addEventListener('stop', () => setTimeout(res, 0), { once: true }); }) : Promise.resolve());
    const ended = Promise.all([stopped(x.part), stopped(x.whole)]);
    x.part?.stop();
    x.whole.stop();
    await ended;
    release();
    await Promise.all(x.sends);
    try {
      const whole = new Blob(x.chunks, { type: x.whole.mimeType || x.mime || 'audio/webm' });
      if (whole.size && whole.size <= WHOLE_MAX) await api.recordings.audio(x.id, whole, nameFor(whole.type, 'meeting')).catch(() => undefined);
      setRec(await api.recordings.stop(x.id, { durationSecs: x.elapsed, marks: marked }));
      setState('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not stop the recording. Try again.');
      setState('done');
    }
  }, [release]);

  /** Throw away a recording that is still running. */
  const discard = useCallback(async () => {
    const x = r.current;
    if (!x) return;
    window.clearInterval(x.tick);
    x.part?.stop(); x.whole.stop();
    release();
    await api.recordings.discard(x.id).catch(() => undefined);
    r.current = null;
    setRec(null); setSeconds(0); setMarks([]); setState('idle');
  }, [release]);

  const reset = useCallback(() => { r.current = null; setRec(null); setSeconds(0); setMarks([]); setError(''); setState('idle'); }, []);

  /** Pick up a recording that has stopped (Record ?recording=id): its minutes being written, or ready to review. */
  const resume = useCallback((x: Recording) => {
    if (r.current || x.status === 'recording') return;
    setRec(x); setSeconds(x.durationSecs ?? 0); setMarks(x.marks); setError(''); setState('done');
  }, []);

  return { state, error, seconds, marks, rec, setRec, level: level as MotionValue<number>, bars, start, pause, mark, stop, discard, reset, resume };
}
